import { useState, useEffect } from 'react'
import supabase from '../../lib/supabase'
import FileUploader from './FileUploader'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const IMAGE_BUCKET = 'images'
const IMAGE_FOLDERS = ['uploads', 'projects']

function normalizeServiceIds(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String)
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map(String)
  }

  return []
}

export default function ProjectsManager() {
  const [projects, setProjects] = useState([])
  const [services, setServices] = useState([])
  const [existingImages, setExistingImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [imagesLoading, setImagesLoading] = useState(false)
  const [editingProject, setEditingProject] = useState(null)
  const [showImageLibrary, setShowImageLibrary] = useState(false)

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_name: '',
    project_url: '',
    image_url: '',
    is_featured: false,
    services_used: []
  })

  useEffect(() => {
    fetchProjects()
    fetchServices()
    fetchExistingImages()
  }, [])

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase
        .from('codevaro_services')
        .select('id, title, active, sort_order')
        .order('sort_order', { ascending: true })
        .order('title', { ascending: true })

      if (error) throw error

      setServices(data || [])
    } catch (error) {
      console.error('Error fetching services:', error)
    }
  }

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('codevaro_projects')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      setProjects(
        (data || []).map((project) => ({
          ...project,
          services_used: normalizeServiceIds(project.services_used)
        }))
      )
    } catch (error) {
      console.error('Error fetching projects:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchExistingImages = async () => {
    try {
      setImagesLoading(true)

      const allImages = []

      for (const folder of IMAGE_FOLDERS) {
        const { data, error } = await supabase.storage
          .from(IMAGE_BUCKET)
          .list(folder, {
            limit: 100,
            offset: 0,
            sortBy: {
              column: 'created_at',
              order: 'desc'
            }
          })

        if (error) {
          console.warn(`Error listing folder ${folder}:`, error)
          continue
        }

        const folderImages = (data || [])
          .filter((file) => {
            const name = file.name?.toLowerCase() || ''

            return (
              name.endsWith('.jpg') ||
              name.endsWith('.jpeg') ||
              name.endsWith('.png') ||
              name.endsWith('.webp') ||
              name.endsWith('.gif') ||
              name.endsWith('.svg')
            )
          })
          .map((file) => {
            const path = `${folder}/${file.name}`

            const { data: publicData } = supabase.storage
              .from(IMAGE_BUCKET)
              .getPublicUrl(path)

            return {
              name: file.name,
              folder,
              path,
              url: publicData?.publicUrl || '',
              created_at: file.created_at || file.updated_at || null
            }
          })

        allImages.push(...folderImages)
      }

      allImages.sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0

        return dateB - dateA
      })

      setExistingImages(allImages)
    } catch (error) {
      console.error('Error fetching existing images:', error)
    } finally {
      setImagesLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const servicesArray = normalizeServiceIds(formData.services_used)

      const projectData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        client_name: formData.client_name?.trim() || null,
        project_url: formData.project_url?.trim() || null,
        image_url: formData.image_url?.trim() || null,
        is_featured: Boolean(formData.is_featured),
        services_used: servicesArray
      }

      let result

      if (editingProject) {
        result = await supabase
          .from('codevaro_projects')
          .update(projectData)
          .eq('id', editingProject.id)
          .select()
      } else {
        result = await supabase
          .from('codevaro_projects')
          .insert([projectData])
          .select()
      }

      if (result.error) throw result.error

      await fetchProjects()
      resetForm()

      alert(editingProject ? 'Το έργο ενημερώθηκε επιτυχώς!' : 'Το έργο δημιουργήθηκε επιτυχώς!')
    } catch (error) {
      console.error('Error saving project:', error)
      alert('Σφάλμα κατά την αποθήκευση: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (project) => {
    setEditingProject(project)

    setFormData({
      title: project.title || '',
      description: project.description || '',
      client_name: project.client_name || '',
      project_url: project.project_url || '',
      image_url: project.image_url || '',
      is_featured: Boolean(project.is_featured),
      services_used: normalizeServiceIds(project.services_used)
    })

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το έργο;')) return

    try {
      const { error } = await supabase
        .from('codevaro_projects')
        .delete()
        .eq('id', id)

      if (error) throw error

      await fetchProjects()
      alert('Το έργο διαγράφηκε επιτυχώς!')
    } catch (error) {
      console.error('Error deleting project:', error)
      alert('Σφάλμα κατά τη διαγραφή: ' + error.message)
    }
  }

  const resetForm = () => {
    setEditingProject(null)
    setShowImageLibrary(false)

    setFormData({
      title: '',
      description: '',
      client_name: '',
      project_url: '',
      image_url: '',
      is_featured: false,
      services_used: []
    })
  }

  const handleImageUpload = async (url) => {
    setFormData((prev) => ({
      ...prev,
      image_url: url
    }))

    await fetchExistingImages()
  }

  const handleChooseExistingImage = (url) => {
    setFormData((prev) => ({
      ...prev,
      image_url: url
    }))

    setShowImageLibrary(false)
  }

  const handleClearImage = () => {
    setFormData((prev) => ({
      ...prev,
      image_url: ''
    }))
  }

  const handleServiceToggle = (serviceId) => {
    const id = String(serviceId)

    setFormData((prev) => {
      const currentServices = normalizeServiceIds(prev.services_used)

      const newServices = currentServices.includes(id)
        ? currentServices.filter((item) => item !== id)
        : [...currentServices, id]

      return {
        ...prev,
        services_used: newServices
      }
    })
  }

  const getServiceNames = (serviceIds) => {
    const ids = normalizeServiceIds(serviceIds)

    if (ids.length === 0) return 'Καμία υπηρεσία'

    const serviceNames = ids
      .map((id) => services.find((service) => String(service.id) === String(id))?.title)
      .filter(Boolean)

    return serviceNames.length > 0 ? serviceNames.join(', ') : 'Άγνωστες υπηρεσίες'
  }

  if (loading && projects.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Διαχείριση Έργων (Portfolio)</h2>
          <p className="mt-1 text-sm text-gray-600">
            Έργα που εμφανίζονται στην αρχική σελίδα για προβολή στους επισκέπτες
          </p>
        </div>

        <button
          onClick={resetForm}
          className="rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700"
        >
          <SafeIcon icon={FiIcons.FiPlus} className="mr-2 inline h-4 w-4" />
          Νέο Έργο Portfolio
        </button>
      </div>

      <div className="rounded-lg bg-white p-6 shadow-md">
        <h3 className="mb-4 text-lg font-semibold">
          {editingProject ? 'Επεξεργασία Έργου' : 'Νέο Έργο Portfolio'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Τίτλος Έργου *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    title: e.target.value
                  }))
                }
                className="w-full rounded-lg border border-gray-300 p-3 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                placeholder="Εισάγετε τον τίτλο του έργου"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Όνομα Πελάτη
              </label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    client_name: e.target.value
                  }))
                }
                className="w-full rounded-lg border border-gray-300 p-3 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                placeholder="Εισάγετε το όνομα του πελάτη"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                URL Έργου
              </label>
              <input
                type="url"
                value={formData.project_url}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    project_url: e.target.value
                  }))
                }
                className="w-full rounded-lg border border-gray-300 p-3 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                placeholder="https://example.com"
              />
            </div>

            <div className="flex items-center">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_featured: e.target.checked
                    }))
                  }
                  className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">Προβεβλημένο Έργο</span>
              </label>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Περιγραφή Έργου *
            </label>
            <textarea
              required
              rows={4}
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value
                }))
              }
              className="w-full rounded-lg border border-gray-300 p-3 focus:border-transparent focus:ring-2 focus:ring-purple-500"
              placeholder="Εισάγετε την περιγραφή του έργου"
            />
          </div>

          <div>
            <div className="mb-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Εικόνα Έργου
              </label>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setShowImageLibrary((prev) => !prev)}
                  className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200"
                >
                  <SafeIcon icon={FiIcons.FiImage} className="mr-1 inline h-4 w-4" />
                  {showImageLibrary ? 'Κλείσιμο Gallery' : 'Επιλογή από υπάρχουσες'}
                </button>

                <button
                  type="button"
                  onClick={fetchExistingImages}
                  className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200"
                >
                  <SafeIcon icon={FiIcons.FiRefreshCw} className="mr-1 inline h-4 w-4" />
                  Refresh
                </button>

                {formData.image_url ? (
                  <button
                    type="button"
                    onClick={handleClearImage}
                    className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 hover:bg-red-100"
                  >
                    Αφαίρεση εικόνας
                  </button>
                ) : null}
              </div>
            </div>

            <FileUploader
              onUpload={handleImageUpload}
              currentUrl={formData.image_url}
              accept="image/*"
              folder="projects"
            />

            {showImageLibrary ? (
              <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">Υπάρχουσες εικόνες</h4>
                    <p className="text-sm text-gray-500">
                      Επιλέξτε μία εικόνα που έχει ήδη ανέβει στο Supabase Storage.
                    </p>
                  </div>
                </div>

                {imagesLoading ? (
                  <div className="flex h-24 items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-purple-600"></div>
                  </div>
                ) : existingImages.length === 0 ? (
                  <div className="rounded-lg bg-white p-4 text-sm text-gray-500">
                    Δεν βρέθηκαν εικόνες στα folders: {IMAGE_FOLDERS.join(', ')}.
                  </div>
                ) : (
                  <div className="grid max-h-96 grid-cols-2 gap-3 overflow-y-auto md:grid-cols-4 lg:grid-cols-6">
                    {existingImages.map((image) => {
                      const selected = formData.image_url === image.url

                      return (
                        <button
                          type="button"
                          key={image.path}
                          onClick={() => handleChooseExistingImage(image.url)}
                          className={`overflow-hidden rounded-lg border bg-white text-left transition ${
                            selected
                              ? 'border-purple-600 ring-2 ring-purple-300'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <img
                            src={image.url}
                            alt={image.name}
                            className="h-24 w-full object-cover"
                          />

                          <div className="p-2">
                            <div className="truncate text-xs font-medium text-gray-700">
                              {image.name}
                            </div>
                            <div className="truncate text-xs text-gray-400">
                              {image.folder}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            ) : null}

            {formData.image_url ? (
              <div className="mt-4 rounded-xl border border-gray-200 p-4">
                <div className="mb-2 text-sm font-medium text-gray-700">Επιλεγμένη εικόνα</div>
                <img
                  src={formData.image_url}
                  alt="Project preview"
                  className="h-40 w-64 rounded-lg border object-cover"
                />
                <div className="mt-2 break-all text-xs text-gray-500">
                  {formData.image_url}
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Υπηρεσίες που Χρησιμοποιήθηκαν σε αυτό το Έργο
            </label>

            {services.length > 0 ? (
              <div className="grid max-h-40 grid-cols-2 gap-2 overflow-y-auto rounded-lg border border-gray-200 p-3 md:grid-cols-3">
                {services.map((service) => {
                  const serviceId = String(service.id)
                  const checked = normalizeServiceIds(formData.services_used).includes(serviceId)

                  return (
                    <label key={service.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleServiceToggle(serviceId)}
                        className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">{service.title}</span>
                    </label>
                  )
                })}
              </div>
            ) : (
              <p className="mt-2 rounded-lg bg-gray-50 p-3 text-sm text-gray-500">
                Δεν υπάρχουν διαθέσιμες υπηρεσίες. Δημιουργήστε πρώτα υπηρεσίες για να τις συνδέσετε με έργα.
              </p>
            )}

            {normalizeServiceIds(formData.services_used).length > 0 ? (
              <div className="mt-2">
                <p className="text-sm text-gray-600">
                  Επιλεγμένες υπηρεσίες: {getServiceNames(formData.services_used)}
                </p>
              </div>
            ) : null}
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center rounded-lg bg-purple-600 px-6 py-2 text-white transition-colors hover:bg-purple-700 disabled:opacity-50"
            >
              {loading ? (
                <SafeIcon icon={FiIcons.FiLoader} className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {loading ? 'Αποθήκευση...' : editingProject ? 'Ενημέρωση Έργου' : 'Δημιουργία Έργου'}
            </button>

            {editingProject ? (
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg bg-gray-500 px-6 py-2 text-white transition-colors hover:bg-gray-600"
              >
                Ακύρωση
              </button>
            ) : null}
          </div>
        </form>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-md">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold">Υπάρχοντα Έργα Portfolio ({projects.length})</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Εικόνα
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Έργο
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Πελάτης
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Κατάσταση
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Υπηρεσίες
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Ενέργειες
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200 bg-white">
              {projects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4">
                    {project.image_url ? (
                      <img
                        src={project.image_url}
                        alt={project.title}
                        className="h-16 w-16 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gray-200">
                        <SafeIcon icon={FiIcons.FiImage} className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{project.title}</div>
                    <div className="max-w-xs truncate text-sm text-gray-500">{project.description}</div>

                    {project.project_url ? (
                      <a
                        href={project.project_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Δείτε το έργο
                      </a>
                    ) : null}
                  </td>

                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                    {project.client_name || 'Δεν καθορίστηκε'}
                  </td>

                  <td className="whitespace-nowrap px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                        project.is_featured
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {project.is_featured ? 'Προβεβλημένο' : 'Κανονικό'}
                    </span>
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">
                      {getServiceNames(project.services_used)}
                    </div>
                  </td>

                  <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleEdit(project)}
                        className="inline-flex items-center text-purple-600 hover:text-purple-900"
                      >
                        <SafeIcon icon={FiIcons.FiEdit2} className="mr-1 h-4 w-4" />
                        Επεξεργασία
                      </button>

                      <button
                        onClick={() => handleDelete(project.id)}
                        className="inline-flex items-center text-red-600 hover:text-red-900"
                      >
                        <SafeIcon icon={FiIcons.FiTrash2} className="mr-1 h-4 w-4" />
                        Διαγραφή
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {projects.length === 0 ? (
            <div className="py-12 text-center">
              <SafeIcon icon={FiIcons.FiFolderPlus} className="mx-auto mb-4 h-12 w-12 text-gray-400" />
              <div className="mb-4 text-gray-500">Δεν βρέθηκαν έργα portfolio</div>

              <button
                onClick={resetForm}
                className="rounded-lg bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700"
              >
                Δημιουργήστε το πρώτο σας έργο
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}